import { Component, Input, OnInit, OnDestroy, HostListener } from '@angular/core';
import { BsModalService } from 'ngx-bootstrap/modal';
import { OrganisationService } from '../../services/organisation/organisation.service';
import { StripeService } from '../../services/stripe/stripe.service';
import { environment } from '../../../environments/environment';
import {
  MODAL_COUNTRY_MAPPING,
  COUNTRIES_WITH_STATE_DROPDOWN,
  STATE_MAPPINGS,
  CountryOption,
} from '../billing/billing.types';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-billing-modal',
  templateUrl: './billing-modal.component.pug',
  styleUrls: ['./billing-modal.component.scss'],
  standalone: false,
})
export class BillingModalComponent implements OnInit, OnDestroy {
  @Input() preSelectedPlan = $localize`Growth`;
  @Input() initialCurrency = '';
  @Input() planUid = '';
  @Input() cbItemPriceId = '';
  @Input() prices: any[] = [];

  cardElement: any;
  expiryElement: any;
  cvvElement: any;
  stripeErrors = {
    cardNumber: '',
    expiry: '',
    cvv: '',
  };

  textValueFirstName = '';
  textValueLastName = '';
  address = '';
  city = '';
  state = '';
  stateCode = '';
  country = '';
  zip = '';
  zipError = false;

  monthlyChecked = false;
  yearlyChecked = true;
  selectedCurrency = '';

  currentPricing = {
    monthly: 160,
    yearly: 140,
  };

  dropDownLabel = $localize`Country`;
  dropDownOptions: Array<{ label: string; value: string }> = [];
  stateDropdownLabel = $localize`State`;
  stateDropdownOptions: Array<{ label: string; value: string }> = [];
  showStateDropdown = false;
  selectedCountry: CountryOption | undefined = undefined;

  isProcessing = false;
  planDescription = '';
  showCurrencyDropdown = false;
  currencies = ['USD', 'AUD', 'NZD'];
  loaded = true;
  cardNumberComplete = false;
  cardExpiryComplete = false;
  cardCvcComplete = false;
  zipErrors = [
    { name: 'invalidZip', msg: $localize`The ZIP you have entered is invalid, please try again.`, enabled: false },
  ];

  // Discount code state
  showDiscountInput = false;
  discountCode = '';
  appliedDiscountCode = '';
  discountErrors = [{ name: 'invalidDiscount', msg: $localize`Invalid discount code`, enabled: false }];
  isApplyingDiscount = false;

  // Estimate state
  estimateLoading = false;
  estimateSubtotal: number | null = null;
  estimateGst: number | null = null;
  estimateTodaysTotal: number | null = null;
  estimateOrderDiscount: number | null = null;
  estimateCurrency = '';

  apiErrorMessage: string = '';
  private apiErrorTimer: any = null;

  private estimateSubscription: Subscription | null = null;
  private billingFieldDebounceTimer: any = null;

  private planDescriptionOption = {
    SS_Starter: $localize`Ideal for organisations with up to 75 employees`,
    SS_Growth: $localize`Ideal for organisations with up to 150 employees`,
    SS_Plus: $localize`Ideal for organisations with up to 300 employees`,
    SS_Premium: $localize`Ideal for organisations with up to 500 employees`,
  };

  get SUBTEXT() {
    return {
      monthly: `$${this.currentPricing.monthly.toFixed(2)} per month, for 12 months`,
      yearly: `$${this.currentPricing.yearly.toFixed(2)} per month, for 12 months`,
    };
  }

  get discountPercentage() {
    const discount = ((this.currentPricing.monthly - this.currentPricing.yearly) / this.currentPricing.monthly) * 100;
    const roundedDiscount = Math.round(discount * 10) / 10;
    return roundedDiscount.toFixed(1);
  }

  constructor(
    private modalService: BsModalService,
    public organisationService: OrganisationService,
    private stripeService: StripeService,
  ) {}

  ngOnInit(): void {
    this.selectedCurrency = this.initialCurrency || 'USD';

    const apiKey = this.getStripeApiKey(environment.STRIPE.DEFAULT_REGION);
    this.stripeService.initStripeKey(apiKey);
    this.stripeService.initStripeElements();
    this.stripeService.updateBillingTimezone(environment.STRIPE.DEFAULT_REGION);

    setTimeout(() => {
      this.initializeStripeElements();
    });

    this.initializeDropdownOptions();
    this.updateCurrentPricing();
    this.planDescription = this.planDescriptionOption['SS_' + this.preSelectedPlan] || '';
  }

  ngOnDestroy() {
    this.unmountStripeElements();
    if (this.estimateSubscription) {
      this.estimateSubscription.unsubscribe();
    }
    if (this.billingFieldDebounceTimer) {
      clearTimeout(this.billingFieldDebounceTimer);
    }
    if (this.apiErrorTimer) {
      clearTimeout(this.apiErrorTimer);
    }
  }

  private unmountStripeElements(): void {
    this.cardElement?.unmount?.();
    this.expiryElement?.unmount?.();
    this.cvvElement?.unmount?.();
  }

  closeModal(): void {
    this.modalService.hide();
  }

  setError(name: string, enabled: boolean) {
    this.zipErrors.find((o) => o.name === name)!.enabled = enabled;
  }

  initializeDropdownOptions(): void {
    this.dropDownOptions = MODAL_COUNTRY_MAPPING.map((country) => ({
      label: country.stripeCode,
      value: country.name,
    }));

    this.dropDownOptions = [
      ...this.dropDownOptions.slice(0, 5),
      ...this.dropDownOptions.slice(5).sort((a, b) => a.value.localeCompare(b.value)),
    ];
  }

  toggleCurrencyDropdown(): void {
    this.showCurrencyDropdown = !this.showCurrencyDropdown;
  }

  selectCurrency(currency): void {
    this.selectedCurrency = currency;
    this.showCurrencyDropdown = false;
    this.updateCurrentPricing();
    this.cbItemPriceId = this.prices?.find(
      (p) =>
        p.currency_display === this.selectedCurrency &&
        p.frequency_display === (this.yearlyChecked ? 'yearly' : 'monthly'),
    )?.cb_item_price_id;
    this.fetchEstimate();
  }

  @HostListener('document:click', ['$event'])
  closeDropdown(event: Event) {
    const target = event.target as HTMLElement;
    if (!target.closest('.dropdown')) {
      this.showCurrencyDropdown = false;
    }
  }

  isStartButtonDisabled(): boolean {
    const hasMissingRequiredFields =
      !this.textValueFirstName ||
      !this.textValueLastName ||
      !this.state ||
      !this.city ||
      !this.address ||
      !this.country;
    const hasStripeErrors = !!this.stripeErrors.cardNumber || !!this.stripeErrors.expiry || !!this.stripeErrors.cvv;
    const hasIncompleteCard = !this.cardNumberComplete || !this.cardExpiryComplete || !this.cardCvcComplete;
    const hasInvalidZip = !!this.zip && this.zipError;
    return hasMissingRequiredFields || hasStripeErrors || hasIncompleteCard || hasInvalidZip;
  }

  async activateAccount(): Promise<void> {
    this.validateZip();
    if (this.isStartButtonDisabled()) return;
    if (this.isProcessing) return;
    this.loaded = false;

    this.isProcessing = true;

    try {
      const { paymentMethod, error: paymentMethodError } = await this.stripeService.createPaymentMethod(
        this.cardElement,
        {
          billing_details: {
            name: `${this.textValueFirstName} ${this.textValueLastName}`,
            address: {
              line1: this.address,
              city: this.city,
              state: this.state,
              postal_code: this.zip,
              country: this.selectedCountry?.stripeCode,
            },
          },
        },
      );

      if (paymentMethodError) {
        this.handlePaymentError(paymentMethodError.message || '');
        return;
      }

      const payload: any = {
        billing_first_name: this.textValueFirstName,
        billing_last_name: this.textValueLastName,
        address: this.address,
        city: this.city,
        state: this.state,
        country: this.selectedCountry?.stripeCode,
        zip: this.zip,
        billing_timezone: this.selectedCountry?.entity,
        payment_method_id: paymentMethod.id,
        plan_uuid: this.planUid,
        frequency: this.yearlyChecked ? 'yearly' : 'monthly',
        currency: this.selectedCurrency,
        item_price_id: this.cbItemPriceId,
      };

      if (this.stateCode) {
        payload.state_code = this.stateCode;
      }

      if (this.appliedDiscountCode) {
        payload.coupon_ids = [this.appliedDiscountCode];
      }

      const response = await this.organisationService.setupStripeBilling(payload).toPromise();

      if (response?.data?.requires_action) {
        const { setup_intent_client_secret, setup_intent_id, stripe_customer_id } = response.data;

        const { setupIntent, error } = await this.stripeService.confirmCardSetup(setup_intent_client_secret, {
          payment_method: paymentMethod.id,
        });

        if (error) {
          this.handlePaymentError(error.message || '');
          return;
        }

        const secondPayload: any = {
          billing_first_name: this.textValueFirstName,
          billing_last_name: this.textValueLastName,
          address: this.address,
          city: this.city,
          state: this.state,
          country: this.selectedCountry?.stripeCode,
          zip: this.zip,
          currency: this.selectedCurrency,
          billing_timezone: this.selectedCountry?.entity,
          setup_intent_id: setup_intent_id,
          stripe_customer_id: stripe_customer_id,
          frequency: this.yearlyChecked ? 'yearly' : 'monthly',
          plan_uuid: this.planUid,
          item_price_id: this.cbItemPriceId,
        };

        if (this.stateCode) {
          secondPayload.state_code = this.stateCode;
        }

        if (this.appliedDiscountCode) {
          secondPayload.coupon_ids = [this.appliedDiscountCode];
        }

        const finalResponse = await this.organisationService.setupStripeBilling(secondPayload).toPromise();

        if (!finalResponse?.data?.success) {
          const msg = typeof finalResponse?.message === 'string' ? finalResponse.message.trim() : '';
          this.handlePaymentError(msg);
          return;
        }

        this.handleSuccessfulSetup();
      } else if (response?.data?.success) {
        this.handleSuccessfulSetup();
      } else {
        const msg = this._declineCodeMessage(response?.data?.details?.decline_code)
          || (typeof response?.message === 'string' ? response.message.trim() : '');
        this.handlePaymentError(msg);
      }
    } catch (error: any) {
      const body = error?.error;
      const declineCode = body?.data?.details?.decline_code;
      const fromApi = typeof body?.message === 'string' ? body.message.trim() : '';
      this.handlePaymentError(this._declineCodeMessage(declineCode) || fromApi);
    }
  }

  private _declineCodeMessage(declineCode?: string): string {
    switch (declineCode) {
      case 'insufficient_funds':
        return $localize`Your card has insufficient funds. Please try a different payment method.`;
      default:
        return '';
    }
  }

  handleSuccessfulSetup(): void {
    this.isProcessing = false;
    this.loaded = true;
    this.closeModal();
    window.location.href = window.location.href.replace('/plan-comparison', '/requests');
  }

  handlePaymentError(message: string): void {
    this.loaded = true;
    const defaultMsg = $localize`Payment unsuccessful. Please check your card details or try a different payment method.`;
    const trimmed = (message ?? '').trim();
    this.apiErrorMessage = trimmed || defaultMsg;
    this.clearApiErrorAfterDelay();
    this.isProcessing = false;
  }

  private clearApiErrorAfterDelay(): void {
    if (this.apiErrorTimer) {
      clearTimeout(this.apiErrorTimer);
    }
    this.apiErrorTimer = setTimeout(() => {
      this.apiErrorMessage = '';
    }, 5000);
  }

  initializeStripeElements(): void {
    if (this.stripeService.stripeInitError) {
      return;
    }

    this.unmountStripeElements();

    this.cardNumberComplete = false;
    this.cardExpiryComplete = false;
    this.cardCvcComplete = false;
    this.stripeErrors = { cardNumber: '', expiry: '', cvv: '' };

    try {
      this.cardElement = this.stripeService.mountElement('cardNumber', '#modal-card-number-element');

      if (this.cardElement) {
        this.cardElement.on('change', (event: any) => {
          const stripErrorMsg = event.error ? event.error.message : '';
          if (stripErrorMsg === 'Your card number is incomplete.') {
            this.stripeErrors.cardNumber = $localize`You're credit card number should be 16 digits, please try again.`;
          } else if (stripErrorMsg === 'Your card number is invalid.') {
            this.stripeErrors.cardNumber = $localize`The credit card number you have entered is invalid, please try again.`;
          } else {
            this.stripeErrors.cardNumber = stripErrorMsg;
          }
          this.cardNumberComplete = event.complete || false;
        });
      }

      this.expiryElement = this.stripeService.mountElement('cardExpiry', '#modal-card-expiry-element');

      if (this.expiryElement) {
        this.expiryElement.on('change', (event: any) => {
          const stripErrorMsg = event.error ? event.error.message : '';
          if (stripErrorMsg === "Your card's expiration year is in the past.") {
            this.stripeErrors.expiry = $localize`This expiration date has already passed, please enter a valid expiration date.`;
          } else if (stripErrorMsg === "Your card's expiration date is incomplete.") {
            this.stripeErrors.expiry = $localize`Please enter a valid expiration date.`;
          } else {
            this.stripeErrors.expiry = stripErrorMsg;
          }
          this.cardExpiryComplete = event.complete || false;
        });
      }

      this.cvvElement = this.stripeService.mountElement('cardCvc', '#modal-card-cvc-element');

      if (this.cvvElement) {
        this.cvvElement.on('change', (event: any) => {
          const stripErrorMsg = event.error ? event.error.message : '';
          if (stripErrorMsg === "Your card's security code is incomplete.") {
            this.stripeErrors.cvv = $localize`Your CVV should be 3 or 4 digits, please try again.`;
          } else {
            this.stripeErrors.cvv = stripErrorMsg;
          }
          this.cardCvcComplete = event.complete || false;
        });
      }
    } catch (error) {
      console.error('Error initializing Stripe elements:', error);
    }
  }

  dropDownOptionSelected(event: { value: string }) {
    const selectedCountry = MODAL_COUNTRY_MAPPING.find((country) => country.name === event.value);

    const button = document.querySelector('[data-dropdown-type="modal-country"]');
    if (button) button.classList.remove('placeholder-text');
    if (button) button.classList.remove('dropdown-border');

    if (selectedCountry) {
      this.selectedCountry = selectedCountry;
      this.country = selectedCountry.name;

      if (this.stripeService.billingTimezone !== selectedCountry.entity) {
        this.stripeService.updateBillingTimezone(selectedCountry.entity);
        const apiKey = this.getStripeApiKey(selectedCountry.entity);
        this.stripeService.initStripeKey(apiKey);
        this.stripeService.initStripeElements();
        this.initializeStripeElements();
      }

      this.updateStateDropdown(selectedCountry.stripeCode);
      this.validateZip();
      this.fetchEstimate();
    }
  }

  updateStateDropdown(countryCode: string): void {
    const hasDropdown = COUNTRIES_WITH_STATE_DROPDOWN.includes(countryCode);
    if (hasDropdown) {
      this.state = '';
      this.stateCode = '';
      this.showStateDropdown = false;
      const states = STATE_MAPPINGS[countryCode] || [];
      this.stateDropdownOptions = states.map((s) => ({ label: s.code, value: s.name }));
      setTimeout(() => {
        this.showStateDropdown = true;
      });
    } else {
      if (this.showStateDropdown) {
        this.state = '';
      }
      this.stateCode = '';
      this.showStateDropdown = false;
      this.stateDropdownOptions = [];
    }
  }

  stateDropdownSelected(event: { value: string }): void {
    const countryCode = this.selectedCountry?.stripeCode;
    if (!countryCode) return;
    const states = STATE_MAPPINGS[countryCode] || [];
    const selected = states.find((s) => s.name === event.value);
    if (selected) {
      this.state = selected.name;
      this.stateCode = selected.code;
      this.onBillingFieldChange();
    }
  }

  getStripeApiKey(billingTimezone: string): string {
    const availableKeys = environment.STRIPE.API_KEYS;
    if (billingTimezone && availableKeys[billingTimezone]) {
      return availableKeys[billingTimezone];
    }
    return availableKeys[environment.STRIPE.DEFAULT_REGION];
  }

  updateCurrentPricing(): void {
    if (!this.prices?.length) return;

    const currencyPrices = this.prices.filter((price) => price.currency_display === this.selectedCurrency);
    const monthlyPrice = currencyPrices.find((p) => p.frequency_display === 'monthly')?.price || 0;
    const yearlyTotal = currencyPrices.find((p) => p.frequency_display === 'yearly')?.price || 0;

    this.currentPricing = {
      monthly: monthlyPrice,
      yearly: yearlyTotal / 12,
    };
  }

  monthlyCheck(check: boolean): void {
    if (check) {
      this.monthlyChecked = true;
      this.yearlyChecked = false;
      this.cbItemPriceId = this.prices?.find(
        (p) => p.currency_display === this.selectedCurrency && p.frequency_display === 'monthly',
      )?.cb_item_price_id;
      this.fetchEstimate();
    }
  }

  yearlyCheck(check: boolean): void {
    if (check) {
      this.yearlyChecked = true;
      this.monthlyChecked = false;
      this.cbItemPriceId = this.prices?.find(
        (p) => p.currency_display === this.selectedCurrency && p.frequency_display === 'yearly',
      )?.cb_item_price_id;
      this.fetchEstimate();
    }
  }

  validateZip(): void {
    if (!this.zip || !this.selectedCountry) {
      this.setError('invalidZip', false);
      this.zipError = false;
      return;
    }

    const zipPattern = this.selectedCountry.zipPattern;
    if (!zipPattern || zipPattern.test(this.zip.trim())) {
      this.setError('invalidZip', false);
      this.zipError = false;
    } else {
      this.setError('invalidZip', true);
      this.zipError = true;
    }
  }

  onZipChange(newValue: string): void {
    this.zip = newValue;
    this.validateZip();
  }

  openChangePlanModal(): void {
    const payload = {
      audit_log: 'SIGNUP_CHANGE_PLAN',
      log: {
        current_selected_plan: this.preSelectedPlan,
      },
    };
    this.organisationService.addAuditLog(payload);
    this.closeModal();
  }

  // Discount code methods
  toggleDiscountInput(): void {
    this.showDiscountInput = true;
  }

  onDiscountCodeChange(): void {
    if (this.discountErrors[0].enabled) {
      this.discountErrors[0].enabled = false;
    }
  }

  applyDiscount(): void {
    if (!this.discountCode.trim()) return;
    this.isApplyingDiscount = true;
    this.discountErrors[0].enabled = false;
    this.fetchEstimate(this.discountCode.trim());
  }

  removeDiscount(): void {
    this.appliedDiscountCode = '';
    this.discountCode = '';
    this.estimateOrderDiscount = null;
    this.showDiscountInput = false;
    this.fetchEstimate();
  }

  // cb-estimate integration
  hasBillingAddress(): boolean {
    return !!(this.selectedCountry && this.address && this.city && this.state);
  }

  onBillingFieldChange(): void {
    if (this.billingFieldDebounceTimer) {
      clearTimeout(this.billingFieldDebounceTimer);
    }
    this.billingFieldDebounceTimer = setTimeout(() => {
      if (this.hasBillingAddress()) {
        this.fetchEstimate();
      }
    }, 500);
  }

  fetchEstimate(couponCode?: string): void {
    if (!this.cbItemPriceId || !this.hasBillingAddress()) {
      this.isApplyingDiscount = false;
      return;
    }

    const billingAddress: any = {
      address: this.address,
      city: this.city,
      state: this.state,
      country: this.selectedCountry!.stripeCode,
    };

    if (this.stateCode) {
      billingAddress.state_code = this.stateCode;
    }

    const payload: any = {
      billing_timezone: this.selectedCountry!.entity,
      billing_address: billingAddress,
      subscription_items: [{ item_price_id: this.cbItemPriceId }],
    };

    const coupons: string[] = [];
    if (couponCode) {
      coupons.push(couponCode);
    } else if (this.appliedDiscountCode) {
      coupons.push(this.appliedDiscountCode);
    }
    if (coupons.length > 0) {
      payload.coupon_ids = coupons;
    }

    if (this.estimateSubscription) {
      this.estimateSubscription.unsubscribe();
    }

    this.estimateLoading = true;
    this.estimateSubscription = this.organisationService.getCbEstimate(payload).subscribe({
      next: (response) => {
        this.estimateLoading = false;
        this.isApplyingDiscount = false;
        const data = response?.data || response;

        this.estimateSubtotal = data.sub_total != null ? data.sub_total / 100 : null;
        this.estimateTodaysTotal = data.total != null ? data.total / 100 : null;
        this.estimateCurrency = data.currency_code || this.selectedCurrency;

        const totalTax = data.taxes?.reduce((sum, t) => sum + (t.amount || 0), 0) || 0;
        this.estimateGst = totalTax > 0 ? totalTax / 100 : null;

        const totalDiscount = data.discounts?.reduce((sum, d) => sum + (d.amount || 0), 0) || 0;
        this.estimateOrderDiscount = totalDiscount > 0 ? totalDiscount / 100 : null;

        if (couponCode) {
          this.appliedDiscountCode = couponCode;
          this.discountCode = '';
          this.discountErrors[0].enabled = false;
        }
      },
      error: (error) => {
        this.estimateLoading = false;
        this.isApplyingDiscount = false;
        if (error?.status === 404 && couponCode) {
          this.discountErrors[0].enabled = true;
        } else {
          console.error('Error fetching estimate:', error);
        }
      },
    });
  }

  get displaySubtotal(): number {
    return this.yearlyChecked ? this.currentPricing.yearly * 12 : this.currentPricing.monthly;
  }

  get displayTodaysTotal(): number | null {
    if (this.estimateTodaysTotal != null) {
      return this.estimateTodaysTotal;
    }
    return this.displaySubtotal;
  }

  formatEstimateAmount(amount: number | null): string {
    if (amount == null) return '';
    return `$${amount.toFixed(2)}`;
  }
}
